import { AnalyticsMetrics } from "../schema/analytics";
import { KpiKey } from "../utils/kpiFilterMap";

export interface KpiRowConfig {
	/** i18n key under `analytics.sections.<section>.<key>` */
	i18nKey: string;
	/** The metric field from the response */
	valueField: keyof AnalyticsMetrics;
	/** The metric field used as the 100% denominator for this row. */
	totalField: keyof AnalyticsMetrics;
	/** If set, the row is clickable and drills through via this KPI key. */
	clickKpi?: KpiKey;
	/** Tailwind bg-* class for the bar. Defaults to bg-blue-action-200. */
	barColorClass?: string;
	/** Mark as the baseline (renders 100%). */
	isBaseline?: boolean;
	/** Show the percentage as the row's primary value instead of the raw count (e.g. a rate row). */
	emphasisPercent?: boolean;
}

// ── Leads funnel ─────────────────────────────────────────────────────
// Stops at Assigned Leads (PREC-3568): reply/positive rates belong to the Campaign funnel below,
// where they are measured against assigned leads instead of the misleading total-leads denominator.
export const LEADS_ROWS: KpiRowConfig[] = [
	{
		i18nKey: "totalLeads",
		valueField: "totalLeads",
		totalField: "totalLeads",
		clickKpi: "totalLeads",
		isBaseline: true,
	},
	{ i18nKey: "foundLeads", valueField: "foundLeads", totalField: "totalLeads" },
	{ i18nKey: "qualifiedLeads", valueField: "qualifiedLeads", totalField: "totalLeads" },
	{ i18nKey: "prio1Leads", valueField: "prio1Leads", totalField: "totalLeads" },
	{ i18nKey: "prio2Leads", valueField: "prio2Leads", totalField: "totalLeads" },
	{ i18nKey: "prio3Leads", valueField: "prio3Leads", totalField: "totalLeads" },
	{
		i18nKey: "assignedLeads",
		valueField: "assignedLeads",
		totalField: "totalLeads",
		clickKpi: "assignedLeads",
	},
];

// ── Campaign funnel (of assignedLeads) ───────────────────────────────
// Every row is measured against assignedLeads (the 100% baseline) so the percentages reflect
// campaign performance, except the closing "Positive Sentiment" rate (positive ÷ all replies).
export const CAMPAIGN_FUNNEL_ROWS: KpiRowConfig[] = [
	{
		i18nKey: "assignedLeads",
		valueField: "assignedLeads",
		totalField: "assignedLeads",
		clickKpi: "assignedLeads",
		isBaseline: true,
	},
	{
		i18nKey: "contactedLeads",
		valueField: "contactedLeads",
		totalField: "assignedLeads",
		clickKpi: "contactedLeads",
	},
	{
		i18nKey: "endedCampaignLeads",
		valueField: "endedCampaignLeads",
		totalField: "assignedLeads",
		clickKpi: "endedCampaignLeads",
	},
	// Auto-replies sit between "ended" and "replied" so the funnel reads honestly: they are
	// not replies (PREC-3949 — totalRepliedLeads no longer counts them) and say nothing about
	// interest; the campaign pauses and resumes when the person is back.
	{
		i18nKey: "oooReplies",
		valueField: "totalOooRepliedLeads",
		totalField: "assignedLeads",
		clickKpi: "oooReplies",
		barColorClass: "bg-status-warning-light",
	},
	{
		i18nKey: "totalRepliedLeads",
		valueField: "totalRepliedLeads",
		totalField: "assignedLeads",
		clickKpi: "totalRepliedLeads",
	},
	{
		i18nKey: "positiveReplies",
		valueField: "totalPositiveRepliedLeads",
		totalField: "assignedLeads",
		clickKpi: "positiveReplies",
		barColorClass: "bg-status-success-light",
	},
	{
		i18nKey: "positiveSentiment",
		valueField: "totalPositiveRepliedLeads",
		totalField: "totalRepliedLeads",
		barColorClass: "bg-status-success-light",
		emphasisPercent: true,
	},
];

// ── Reply Analysis (of totalRepliedLeads) ────────────────────────────
export const REPLY_ANALYSIS_ROWS: KpiRowConfig[] = [
	{
		i18nKey: "replyInterested",
		valueField: "totalPositiveRepliedLeads",
		totalField: "totalRepliedLeads",
		clickKpi: "replyInterested",
		barColorClass: "bg-status-success-light",
	},
	{
		i18nKey: "replyBadTiming",
		valueField: "totalBadTimingRepliedLeads",
		totalField: "totalRepliedLeads",
		clickKpi: "replyBadTiming",
		barColorClass: "bg-status-warning-light",
	},
	{
		i18nKey: "replyWrongPerson",
		valueField: "totalWrongPersonRepliedLeads",
		totalField: "totalRepliedLeads",
		clickKpi: "replyWrongPerson",
		barColorClass: "bg-status-wrong-person-light",
	},
	{
		i18nKey: "replyNotInterested",
		valueField: "totalNotInterestedRepliedLeads",
		totalField: "totalRepliedLeads",
		clickKpi: "replyNotInterested",
		barColorClass: "bg-status-error-light",
	},
	{
		i18nKey: "replyNeutral",
		valueField: "totalNeutralRepliedLeads",
		totalField: "totalRepliedLeads",
		clickKpi: "replyNeutral",
		barColorClass: "bg-status-info-light",
	},
];

// ── Email ────────────────────────────────────────────────────────────
export const EMAIL_ROWS: KpiRowConfig[] = [
	{
		i18nKey: "emailsSent",
		valueField: "emailsSendUnique",
		totalField: "emailsSendUnique",
		isBaseline: true,
	},
	{ i18nKey: "emailOpened", valueField: "emailOpened", totalField: "emailsSendUnique" },
	{ i18nKey: "emailClicked", valueField: "emailClicked", totalField: "emailsSendUnique" },
	{ i18nKey: "emailReplies", valueField: "emailReplies", totalField: "emailsSendUnique" },
	{
		i18nKey: "emailPositiveReplied",
		valueField: "emailPositiveReplied",
		totalField: "emailsSendUnique",
		barColorClass: "bg-status-success-light",
	},
	{
		i18nKey: "emailUnsubscribed",
		valueField: "emailUnsubscribed",
		totalField: "emailsSendUnique",
		barColorClass: "bg-status-warning-light",
	},
	{
		i18nKey: "emailBounced",
		valueField: "emailBounced",
		totalField: "emailsSendUnique",
		barColorClass: "bg-status-error-light",
	},
];

// ── LinkedIn ─────────────────────────────────────────────────────────
export const LINKEDIN_ROWS: KpiRowConfig[] = [
	{
		i18nKey: "linkedinInviteSent",
		valueField: "linkedinInviteSent",
		totalField: "linkedinInviteSent",
		isBaseline: true,
	},
	{
		i18nKey: "linkedinInvitesAccepted",
		valueField: "linkedinInvitesAccepted",
		totalField: "linkedinInviteSent",
	},
	{ i18nKey: "linkedinReplies", valueField: "linkedinReplies", totalField: "linkedinInviteSent" },
	{
		i18nKey: "linkedinPositiveReplied",
		valueField: "linkedinPositiveReplied",
		totalField: "linkedinInviteSent",
		barColorClass: "bg-status-success-light",
	},
];
